<template>
  <div
    class="grid h-full min-h-0 grid-cols-1 grid-rows-1 overflow-hidden bg-hazel-50 text-gray-900"
  >
    <div
      v-if="summary"
      class="col-start-1 row-start-1 h-full min-h-0 w-full overflow-hidden transition-[opacity,transform] duration-500 ease-in-out will-change-[opacity,transform]"
      :class="
        isContractScreen || isDoneScreen
          ? 'pointer-events-none scale-[0.98] opacity-0'
          : 'scale-100 opacity-100'
      "
    >
      <div class="relative h-full min-h-0 w-full overflow-hidden">
        <div
          class="absolute inset-y-0 left-0 z-10 flex w-[40%] items-center py-8 pl-10 pr-6 transition-transform duration-500 ease-in-out will-change-transform"
          :class="showRightPanel ? 'translate-x-0' : 'translate-x-[75%]'"
        >
          <CounterSignatureVehicleColumn :summary="summary" />
        </div>

        <div
          v-if="showRightPanel"
          class="absolute inset-y-0 right-0 flex w-[60%] min-w-0 flex-col overflow-y-auto overscroll-none py-8 pr-10 transition-opacity duration-500 ease-in-out"
          :class="showRightPanel ? 'opacity-100' : 'pointer-events-none opacity-0'"
        >
          <div class="my-auto w-full">
            <div class="flex w-full flex-col rounded-lg bg-white p-6 shadow-sm md:p-8">
              <AnimatedHeightTransition>
                <div :key="screen" class="w-full">
                  <CounterSignatureBookingSummary
                    v-if="screen === CustomerScreen.BOOKING_SUMMARY && summary"
                    :summary="summary"
                  />
                  <CounterSignatureDeposit
                    v-else-if="screen === CustomerScreen.DEPOSIT_PAYMENT && summary"
                    :summary="summary"
                  />
                  <CounterSignatureContact
                    v-else-if="screen === CustomerScreen.CONTACT_CONFIRMATION"
                    :counter-id="counterId"
                    :email="summary?.customerEmail ?? ''"
                    :phone="summary?.customerPhone ?? ''"
                    :contact-confirmed="session.gates?.customerContactConfirmed ?? false"
                    @confirmed="emit('session-updated')"
                  />
                </div>
              </AnimatedHeightTransition>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Transition name="overlay-fade">
      <div
        v-if="isContractScreen"
        key="contract"
        class="z-10 col-start-1 row-start-1 h-full min-h-0 w-full overflow-hidden"
      >
        <CounterSignatureContract
          :counter-id="counterId"
          :booking-id="session.bookingId"
          :vehicle-categories="summary?.vehicleCategories"
          :custom-contract-checkbox-labels="summary?.customContractCheckboxLabels"
          class="h-full"
          @signed="emit('session-updated')"
        />
      </div>
    </Transition>

    <Transition name="overlay-fade">
      <div
        v-if="isDoneScreen"
        key="done"
        class="z-10 col-start-1 row-start-1 flex h-full min-h-0 w-full items-center justify-center overflow-hidden bg-hazel-50 p-8"
      >
        <CounterSignatureDone />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { CustomerScreen, type ProcessSessionResponseDto } from '@carvia/ros-client-types';
import AnimatedHeightTransition from '@/components/AnimatedHeightTransition.vue';
import { resolveCounterDisplayScreen } from '@/utils/resolve-customer-screen';
import CounterSignatureBookingSummary from './CounterSignatureBookingSummary.vue';
import CounterSignatureContact from './CounterSignatureContact.vue';
import CounterSignatureContract from './CounterSignatureContract.vue';
import CounterSignatureDeposit from './CounterSignatureDeposit.vue';
import CounterSignatureDone from './CounterSignatureDone.vue';
import CounterSignatureVehicleColumn from './CounterSignatureVehicleColumn.vue';
import { computed } from 'vue';

const props = defineProps<{
  session: ProcessSessionResponseDto;
  counterId: string;
}>();

const emit = defineEmits<{
  (e: 'session-updated'): void;
}>();

const screen = computed(() =>
  resolveCounterDisplayScreen({
    agentProcessStep: props.session.agentProcessStep,
    counterId: props.session.counterId ?? props.counterId,
    customerScreen: props.session.customerScreen,
    gates: props.session.gates,
  }),
);
const summary = computed(() => props.session.bookingSummary);
const isContractScreen = computed(() => screen.value === CustomerScreen.CONTRACT_SIGNATURE);
const isDoneScreen = computed(() => screen.value === CustomerScreen.SIGNATURE_DONE);
const showRightPanel = computed(() => screen.value !== CustomerScreen.IDLE);
</script>

<style scoped>
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition:
    opacity 500ms ease,
    transform 500ms ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
  transform: scale(1.02);
}
</style>
